/**
 * Composible validation helpers
 * @module
 */
import * as _ from 'lodash'

/** Alkali validation type */
export type Validation = any[] & { isValid?: boolean }
type ValidationFn = (...args: any[]) => Validation
type PredicateFn = (...args: any[]) => boolean

export const validationResult = (isValid = true): Validation => Object.assign([], { isValid })
export const validResult = validationResult

const validationStack = []
/**
 * Establishes or joins a validation context in which errors are recursively composed from nested validations
 */
export const validationCtx = (label: string, fn: ValidationFn): ValidationFn => (...args) => {
	validationStack.push([])
	let result
	try {
		result = fn(...args)
	} finally {
		const curFrame = validationStack.pop()
		if (result) {
			result.push(...curFrame)
			if (validationStack.length > 0) {
				const last = validationStack[validationStack.length - 1]
				validationStack[validationStack.length - 1] = _.uniq(last.concat(label ? result.map(e => label + ': ' + e) : result))
			}
		}
	}
	return result
}

export const normalize = v => typeof v == 'string' ? v.trim() : v
export const empty = v =>
	v == null ||
	((typeof v === 'string') && v.trim().length == 0) ||
	(!((typeof v === 'number') || (v instanceof Number)) && _.isEmpty(v))

export const notEmpty = v => !empty(v)

export const validPositiveNumber = s => {
	const n = Number(s)
	return Number.isFinite(n) && n >= 0
}

export const validStrictlyPositiveNumber = s => {
	const n = Number(s)
	return Number.isFinite(n) && n > 0
}

export const validPositiveInteger = s => {
	const n = Number(s)
	return Number.isInteger(n) && n >= 0
}

/**
 * Helper that turns a simple PredicateFn into a ValidationFn (validation status and string error message)
 */
export const validateFn = (fn: PredicateFn, errMsg?: string, emptyMsg: string | boolean = 'Must not be empty'): ValidationFn => (val, ...args) => {
	const result = [] as Validation
	val = normalize(val)
	const isEmpty = empty(val)
	let valid
	if (isEmpty && emptyMsg !== true) {
		valid = false
		if (emptyMsg) result.push(emptyMsg)
	} else {
		valid = fn(val, ...args)
		if (!valid && errMsg) result.push(errMsg)
	}
	result.isValid = valid
	return result
}

const orValid = (a, b) => { a.isValid |= b.isValid; return a }
const andValid = (a, b) => { a.isValid &= b.isValid; return a }

export const composeValidation = (reducer, ...fns) => (vals) => fns.map((fn, i) => fn(vals[i])).reduce((result, r2) => {
	reducer(result, r2)
	result.push(...r2)
	return result
})

export const orValidation = composeValidation.bind(null, orValid)
export const andValidation = composeValidation.bind(null, andValid)

/* some simple validations */
export const notEmptyFn = validateFn(val => notEmpty(val))
export const notEmptySetFn = validateFn(set => set.size > 0)
export const notEmptyArrayFn = validateFn(arr => arr?.length > 0)
export const validPositiveNum = validateFn(s => validPositiveNumber(s), 'Must be positive number')
export const emptyOrValidPositiveNum = validateFn(s => validPositiveNumber(s), 'Must be positive number', true)